import { useOrganisationList } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { sortBy } from "@inrange/utils";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import { Link } from "react-router-dom";
import Select from "react-select";
import Loading from "../Loading";
import CreateOrg from "./CreateOrg";
import OrgListSiteMap from "./OrgListSiteMap";

const OrgList: React.FC = () => {
  document.title = `Organisation List`;
  const { fetchOrganisations } = useOrganisationList();
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterOrgTypes, setFilterOrgTypes] = useState<string[]>([]);
  const [displayOrgs, setDisplayOrgs] = useState<Organisation[]>([]);
  const [sortColumn, setSortColumn] = useState<string>("name");

  const orgTypeOptions = useMemo(() => {
    if (!fetchOrganisations.data?.organisations) return [];
    const labels = new Set(
      fetchOrganisations.data.organisations.map((org) =>
        org.isTest ? `Test ${org.orgType}` : org.orgType
      )
    );
    const order = ["Landlord", "Tenant", "Funder", "EPC", "Test Landlord", "Test Tenant"];
    return order
      .filter((t) => labels.has(t))
      .map((t) => ({ value: t, label: t }));
  }, [fetchOrganisations.data?.organisations]);

  useEffect(() => {
    if (!fetchOrganisations.data?.organisations) return;

    let sortedOrgs = fetchOrganisations.data?.organisations;
    if (sortColumn === "name") {
      sortedOrgs.sort(
        sortBy((org: Organisation) =>
          org.name.trim().replaceAll(" ", "").toLowerCase()
        )
      );
    }

    if (sortColumn === "siteCount") {
      sortedOrgs = sortedOrgs.sort((a, b) => {
        if (countSites(a) < countSites(b)) return 1;
        if (countSites(a) > countSites(b)) return -1;
        return 0;
      });
    }

    if (searchValue.length > 1) {
      sortedOrgs = sortedOrgs.filter((org) =>
        org.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (filterOrgTypes.length > 0) {
      sortedOrgs = sortedOrgs.filter((org) => {
        const label = org.isTest ? `Test ${org.orgType}` : org.orgType;
        return filterOrgTypes.includes(label);
      });
    }

    setDisplayOrgs([...sortedOrgs]);
  }, [fetchOrganisations.data?.organisations, searchValue, sortColumn, filterOrgTypes]);

  if (fetchOrganisations.isLoading)
    return <Loading label="Loading organisations..." />;

  return (
    <Container fluid style={{ padding: "12px 12px 0px 12px" }}>
      <Row>
        <Col>
          <CreateOrg />
        </Col>
      </Row>
      <OrgListView
        organisations={displayOrgs}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setSortColumn={setSortColumn}
        orgTypeOptions={orgTypeOptions}
        setFilterOrgTypes={setFilterOrgTypes}
      />
      <OrgListSiteMap />
    </Container>
  );
};

const countSites = (org: Organisation): number => {
  return org.siteOwnerships?.length || 0;
};

const OrgListView: React.FC<{
  organisations: Organisation[];
  searchValue: string;
  setSearchValue: (value: string) => void;
  setSortColumn: (column: string) => void;
  orgTypeOptions: { value: string; label: string }[];
  setFilterOrgTypes: (types: string[]) => void;
}> = ({
  organisations,
  searchValue,
  setSearchValue,
  setSortColumn,
  orgTypeOptions,
  setFilterOrgTypes,
}) => {
  return (
    <>
      <Row style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          <h3>Organisations ({organisations.length})</h3>
        </Col>
        <Col>
          <Select
            isMulti
            placeholder="Filter by org type..."
            options={orgTypeOptions}
            onChange={(values) =>
              setFilterOrgTypes(values.map((v) => v.value))
            }
          />
        </Col>
        <Col>
          <OrgSearchBox
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Col>
      </Row>

      <Table striped bordered responsive>
        <thead>
          <tr>
            <th
              style={{ cursor: "pointer" }}
              onClick={() => setSortColumn("name")}
            >
              Name
            </th>
            <th>Users</th>
            <th
              style={{ cursor: "pointer" }}
              onClick={() => setSortColumn("siteCount")}
              align="right"
            >
              Number of sites
            </th>
          </tr>
        </thead>
        <tbody>
          {organisations.map((org) => (
            <OrgRow key={org.id} org={org} />
          ))}
        </tbody>
      </Table>
    </>
  );
};

const OrgRow: React.FC<{ org: Organisation }> = ({ org }) => {
  const siteCount = countSites(org);
  const orgIsTestString = org.isTest ? "Test " : "";
  return (
    <tr key={org.id}>
      <td>
        <div>
          <Link
            to={`/organisation/${org.id}/edit`}
            data-testid={`org-id-link-${org.id}`}
          >
            <strong>{org.name}</strong>
          </Link>
        </div>
        <div style={{ fontSize: 10, color: "gray" }}>
          {orgIsTestString + org.orgType}
          {org.defaultInvestmentModel
            ? ` (${org.defaultInvestmentModel})`
            : ""}{" "}
          | {org.id}
        </div>
      </td>
      <td style={{ fontSize: 12 }}>
        {org.users.length > 0 && (
          <>
            Users:
            <ul>
              {org.users.map((user) => (
                <li key={user.email}>{user.email}</li>
              ))}
            </ul>
          </>
        )}
      </td>
      <td align="center">{siteCount}</td>
    </tr>
  );
};

const OrgSearchBox: React.FC<{
  searchValue: string;
  setSearchValue: (value: string) => void;
}> = ({ searchValue, setSearchValue }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchValue(inputValue);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Form.Control
        type="search"
        placeholder="Search"
        value={searchValue}
        onChange={handleInputChange}
      />
    </Form>
  );
};

export default OrgList;
